import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMedioMayoristaCart } from "../../context/MedioMayoristaCartContext";

const formatCurrency = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const parseNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeString = (value) => {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
};

const MedioMayoristaResinCard = ({ product }) => {
  const { addItem, openCart } = useMedioMayoristaCart();
  const [quantity, setQuantity] = useState(1);

  const basePrice = parseNumber(product.base_price);
  const finalPrice = parseNumber(product.final_price, basePrice);
  const discount = parseNumber(product.discount);
  const discountPercent = Math.max(0, Math.round(discount * 100));
  const savings = Math.max(0, basePrice - finalPrice);

  const imageSrc = useMemo(
    () => normalizeString(product.image_url) || "/ecommerce/Resinas.png",
    [product.image_url]
  );

  const detailColumns = useMemo(() => {
    const columns = [];
    const viscosity = normalizeString(product.viscosity);
    const density = normalizeString(product.density_g_cm3_20c);

    if (viscosity) columns.push({ label: "Viscosidad", value: viscosity });
    if (density) columns.push({ label: "Densidad", value: density });
    return columns;
  }, [product.density_g_cm3_20c, product.viscosity]);

  const addCurrentProduct = () => {
    addItem({
      type: "medio-mayorista-resina",
      name: product.name,
      unitPrice: finalPrice,
      quantity,
      payload: {
        resin_id: product.id,
      },
    });
    openCart();
  };

  return (
    <article className="relative mx-auto w-full max-w-[440px] overflow-hidden rounded-xl border border-[#7e7e7e] bg-[#efefef] text-[#111] shadow-[0_8px_20px_rgba(0,0,0,0.16)]">
      <Link to={`/productos/medio-mayorista/resina/${product.id}`} className="group block">
        {discountPercent > 0 && (
          <div className="absolute left-2 top-2 z-10 bg-[#e71318] px-2 py-1 leading-none shadow-lg md:left-3 md:top-3">
            <span className="text-base font-extrabold text-white md:text-2xl">-{discountPercent}%</span>
          </div>
        )}

        <div className="flex aspect-[5/4] w-full items-center justify-center bg-[#1f1f1f] p-2 md:p-3">
          <img
            src={imageSrc}
            alt={product.name}
            className="h-full w-full object-contain transition duration-200 group-hover:scale-[1.02]"
            loading="lazy"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = "/ecommerce/Resinas.png";
            }}
          />
        </div>

        <div className="space-y-2 p-3 md:space-y-2.5 md:p-4">
          <h3 className="text-left text-2xl font-extrabold uppercase leading-none text-[#111] md:text-3xl">
            {product.name}
          </h3>

          {detailColumns.length > 0 && (
            <div className="space-y-0.5 border-b border-[#868686] pb-2 md:pb-3">
              {detailColumns.slice(0, 2).map((column) => (
                <div key={column.label} className="flex items-center justify-between text-sm text-[#222] md:text-base">
                  <span>{column.label}:</span>
                  <span>{column.value}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-end gap-2 md:gap-3">
            <span className="text-3xl font-black leading-none text-black md:text-4xl">
              {formatCurrency.format(finalPrice)}
            </span>
            <span className="pb-0.5 text-2xl leading-none text-[#222] md:text-3xl">kg</span>
          </div>

          <div className="flex items-center gap-3 text-sm text-[#585858] md:text-base">
            {basePrice > finalPrice && (
              <span className="line-through decoration-[2px]">{formatCurrency.format(basePrice)}</span>
            )}
            {savings > 0 && <span>• Ahorra {formatCurrency.format(savings)}</span>}
          </div>
        </div>
      </Link>

      <div className="flex items-center justify-between gap-3 px-3 pb-3 md:px-4 md:pb-4">
        <div className="inline-flex overflow-hidden rounded-md border border-[#9e9e9e] bg-[#ececec] text-[#1f2527]">
          <button
            type="button"
            onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
            className="px-3 py-1 text-xl leading-none hover:bg-[#d8d8d8] md:px-3 md:text-2xl"
            aria-label="Disminuir cantidad"
          >
            -
          </button>
          <span className="px-4 py-1 text-xl leading-none md:text-2xl">{quantity}</span>
          <button
            type="button"
            onClick={() => setQuantity((prev) => prev + 1)}
            className="px-3 py-1 text-xl leading-none hover:bg-[#d8d8d8] md:px-3 md:text-2xl"
            aria-label="Aumentar cantidad"
          >
            +
          </button>
        </div>
        <button
          type="button"
          onClick={addCurrentProduct}
          className="rounded-md bg-black px-4 py-2 text-sm font-bold leading-none text-white transition hover:brightness-110 md:min-w-[108px] md:px-5 md:text-lg"
        >
          Agregar
        </button>
      </div>
    </article>
  );
};

export default MedioMayoristaResinCard;
